import { React, useEffect, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Bar, Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Plugin,
    ArcElement
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Box, Table, TableRow, TableCell, Button, Typography, TableHead, TableBody, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BatteryCharging20, Pages } from '@mui/icons-material';
// import { html2pdf } from 'html2pdf.js';
import html2pdf from 'html2pdf.js';
import { format } from 'date-fns';
import Logo from 'src/components/Logo';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend, ChartDataLabels);

const FormComponent = ({ barGraphData, cameraWiseData, Data, Month, Year, clientName, clientId, Location }) => {
    const theme = useTheme();
    const tableHeaderTextColor = theme.palette.mode === 'dark' ? '#ffffff' : '#000000';
    const tableCellColor = '#41667D';

    const formatEventTime = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return format(new Date(dateString), 'MMM-dd-yyyy hh:mm a');
        } catch (error) {
            return 'Invalid Date';
        }
    };

    // console.log(barGraphData,Data,Month,Year)

    let cameras, total_event_count, true_event_count, escalated_event_count, top_camera_name, top_event_type, protocol_wise_counts, uptime_percentage;
    if (Data && Data[0]) {
        ({ cameras, total_event_count, true_event_count, escalated_event_count, top_camera_name, top_event_type, protocol_wise_counts, uptime_percentage } = Data[0]);
    }
    //console.log('clientname',clientName)
    const contentRef = useRef();
    const [observationInput, setObservationInput] = useState("");
    const [observations, setObservations] = useState([]);
    const [hideInputs, setHideInputs] = useState(false);
    const [hideHeading, setHideHeading] = useState(false)

      const transformProtocolData = () => {
        if (!protocol_wise_counts) return null;

        // Group by SubType and sum TrueCount only
        const groupedData = {};

        protocol_wise_counts.forEach(protocol => {
            const subtype = protocol.SubType;
            if (!groupedData[subtype]) {
                groupedData[subtype] = {
                    trueCount: 0
                };
            }
            groupedData[subtype].trueCount += protocol.TrueCount || 0;
        });

        // Filter out categories with zero count and convert to chart format
        const filteredData = Object.entries(groupedData)
            .filter(([label, data]) => data.trueCount > 0)
            .sort((a, b) => b[1].trueCount - a[1].trueCount); // Optional: sort by count descending

        const labels = filteredData.map(([label]) => label);
        const escalationRequired = filteredData.map(([_, data]) => data.trueCount);

        return {
            labels,
            datasets: [
                {
                    label: 'Escalation Required',
                    data: escalationRequired,
                    backgroundColor: 'rgba(255, 99, 132, 0.8)',
                    borderColor: 'rgba(255, 99, 132, 1)',
                    borderWidth: 1,
                    // barThickness: 16,
                    barPercentage: 0.5,
                    categoryPercentage: 0.8,
                    borderRadius: 2,
                    maxBarThickness: 30
                }
            ]
        };
    };

    const chartData = transformProtocolData();

    // Calculate dynamic max value for x-axis
    const calculateMaxValue = () => {
        if (!chartData || chartData.datasets[0].data.length === 0) return 10;

        const maxDataValue = Math.max(...chartData.datasets[0].data);

        // If max value is 1, set max to 2
        if (maxDataValue === 1) return 2;

        // For other values, add some padding (20%) and round up to nearest integer
        return Math.ceil(maxDataValue * 1.2);
    };

    // Calculate step size for x-axis
    const calculateStepSize = () => {
        const maxValue = calculateMaxValue();

        // For small ranges, use smaller step sizes
        if (maxValue <= 5) return 1;
        if (maxValue <= 10) return 2;
        if (maxValue <= 20) return 5;
        return Math.ceil(maxValue / 5);
    };


    const emptyChartData = {
        labels: [],
        datasets: [
            {
                label: 'Escalation Required',
                data: [],
                backgroundColor: 'rgba(255, 99, 132, 0.8)',
                borderColor: 'rgba(255, 99, 132, 1)',
                borderWidth: 1,
                barThickness: 16,
                borderRadius: 2,
            }
        ]
    };

    const options = {
        indexAxis: 'y',
        responsive: true,
        //devicePixelRatio: 2, 
        plugins: {
            legend: {
                display: false
            },
            datalabels: {
                color: '#000',
                font: {
                    weight: 'bold',
                    size: 12
                },
                formatter: (value) => {
                    return value > 0 ? value : '';
                },
                anchor: 'end',
                align: 'end'
            },
            tooltip: {
                callbacks: {
                    label: (context) => `${context.dataset.label}: ${context.raw}`
                }
            }
        },
        scales: {
            x: {
                stacked: false,
                min: 0,
                grid: {
                    display: false,
                    drawBorder: false // removes that extra vertical line
                },
                max: calculateMaxValue(),
                //max: Math.max(...chartData.datasets[0].data),
                ticks: {
                    stepSize: calculateStepSize(),
                    precision: 0 // Ensure no decimal values
                },
                title: {
                    display: true,
                    text: 'Number of Events'
                }
            },
            y: {
                stacked: false,
                grid: {
                    display: false,
                    drawBorder: false
                },

            }
        }
    };




    const grandTotalNotRequired = cameraWiseData.reduce((sum, row) => sum + row.false_event_count, 0);
    const grandTotalRequired = cameraWiseData.reduce((sum, row) => sum + row.true_event_count, 0);
    const grandTotalAll = cameraWiseData.reduce((sum, row) => sum + row.total_audits, 0);

    const splitTableData = (data, itemsPerPage) => {
        const pages = [];
        for (let i = 0; i < data.length; i += itemsPerPage) {
            pages.push(data.slice(i, i + itemsPerPage));
        }
        return pages;
    };

    // const [pages,setPage]=useState([])
    const ItemsPerPage = 10;

    const pdfRef = useRef();


    const calculateAvailableSpace = (element) => {
        const cameraSection = element.querySelector('#camera-wise-section');
        if (!cameraSection) return true;

        // Get the position and height of camera section
        const cameraRect = cameraSection.getBoundingClientRect();
        const cameraBottom = cameraRect.bottom;

        // A4 landscape height in pixels (approx)
        const pageHeight = 1122;

        // Calculate remaining space on current page
        const currentPageBottom = Math.ceil(cameraBottom / pageHeight) * pageHeight;
        const remainingSpace = currentPageBottom - cameraBottom;

        // Estimate escalated section height (header + some rows)
        const estimatedEscalatedHeight = 100 + (5 * 30); // header + ~5 rows

        console.log(`Remaining space: ${remainingSpace}px, Estimated needed: ${estimatedEscalatedHeight}px`);

        return remainingSpace >= estimatedEscalatedHeight;
    };

    const downloadPDF = () => {
        setHideInputs(true);
        if (observations.length === 0) {
            setHideHeading(true);
        }

        setTimeout(() => {
            const element = pdfRef.current;

            if (!element) {
                console.error('PDF element not found');
                setHideInputs(false);
                setHideHeading(false);
                return;
            }

            // Apply table header styling
            const tableHeaders = element.querySelectorAll('.MuiTableCell-head');
            tableHeaders.forEach(header => {
                header.style.paddingTop = '1px';
                header.style.paddingBottom = '12px';
                header.style.paddingLeft = '5px';
                header.style.paddingRight = '5px';
                header.style.verticalAlign = 'middle';
                header.style.textAlign = 'center';
            });

            // Apply smart page breaks
            applySmartPageBreaks(element);

            const tableCols = element.querySelectorAll(".tableCol");
            tableCols.forEach(el => el.classList.remove("tableCol"));

            let month = Month.slice(0, 3).toUpperCase();

            const options = {
                margin: 1,
                filename: `${clientName}${Location ? '-' + Location : ''}_${month}_${Year}.pdf`,
                
                image: { type: 'jpeg', quality: 0.98 },
                // html2canvas: {
                //     scale: 4,
                //     useCORS: true,
                //     allowTaint: true,
                //     logging: true,
                //     letterRendering: true,
                //     onclone: (doc) => {
                //         const pdfRoot = doc.querySelector('#pdf-content');
                //         if (!pdfRoot) return;
                //         pdfRoot.classList.add('pdf-export');
                //         pdfRoot.querySelectorAll('.MuiTableCell-head').forEach(header => {
                //             header.style.setProperty('color', tableHeaderTextColor, 'important');
                //         });
                //     }
                // },
                html2canvas: {
    scale: 4, // Increase from 4 to 5-6 for better quality
    useCORS: true,
    allowTaint: true,
    logging: true,
    letterRendering: true,
    // Add these options for better text rendering
    backgroundColor: '#ffffff',
    onclone: (doc) => {
        const pdfRoot = doc.querySelector('#pdf-content');
        if (!pdfRoot) return;
        pdfRoot.classList.add('pdf-export');
        pdfRoot.querySelectorAll('.MuiTableCell-head').forEach(header => {
            header.style.setProperty('color', tableHeaderTextColor, 'important');
        });
        
        // Force chart to redraw with higher quality
        const chartCanvas = doc.querySelector('canvas');
        if (chartCanvas) {
            // Ensure canvas has proper dimensions
            chartCanvas.style.width = '100%';
            chartCanvas.style.height = '100%';
        }
    }
},
                
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
                pagebreak: {
                    mode: 'css',
                    avoid: ['tr', '.observation-item'] // Prevent breaking table rows and observation items
                }
            };

            html2pdf()
                .from(element)
                .set(options)
                .save()
                .then(() => {
                    cleanupAfterPDF(element);
                    setHideInputs(false);
                    setHideHeading(false);
                })
                .catch((error) => {
                    console.error('PDF generation failed:', error);
                    cleanupAfterPDF(element);
                    setHideInputs(false);
                    setHideHeading(false);
                });
        }, 100);
    };


    const applySmartPageBreaks = (element) => {
        if (!element) return;

        const sections = [
            '#camera-wise-section',
            '#escalated-events-section',
            '#observations-section'
        ];

        sections.forEach(selector => {
            const section = element.querySelector(selector);
            if (section) {
                section.classList.remove('force-new-page');
            }
        });

        const cameraSection = element.querySelector('#camera-wise-section');
        const escalatedSection = element.querySelector('#escalated-events-section');
        const observationsSection = element.querySelector('#observations-section');

        // Smart logic for camera section -> escalated section
        if (cameraSection && escalatedSection) {
            const cameraHeight = cameraSection.offsetHeight;
            const pageHeightThreshold = 600;

            if (cameraHeight > pageHeightThreshold) {
                escalatedSection.classList.add('force-new-page');
                console.log('Camera section is tall, forcing new page for escalated events');
            }
        }


        if (escalatedSection && observationsSection) {
            const escalatedHeight = escalatedSection.offsetHeight;
            const observationsHeight = observationsSection.offsetHeight || 0;
            const pageHeightThreshold = 600;


            if (escalatedHeight > pageHeightThreshold && observationsHeight > 200) {
                observationsSection.classList.add('force-new-page');
                console.log('Escalated section is tall, forcing new page for observations');
            }
        }
    };

    // Cleanup function
    const cleanupAfterPDF = (element) => {
        if (!element) return;

        const escalatedSection = element.querySelector('#escalated-events-section');
        if (escalatedSection) {
            escalatedSection.classList.remove('force-new-page');
        }

        // Restore table header styles
        const tableHeaders = element.querySelectorAll('.MuiTableCell-head');
        tableHeaders.forEach(header => {
            header.style.padding = '';
        });
    };


    const checkSpaceForEscalatedSection = (element) => {
        const escalatedSection = element.querySelector('#escalated-events-section');
        const cameraSection = element.querySelector('#camera-wise-section');

        if (!escalatedSection || !cameraSection) return;

        // Calculate approximate heights
        const cameraHeight = cameraSection.offsetHeight;
        const escalatedHeadingHeight = 50; // Approximate height of heading
        const tableHeaderHeight = 40; // Approximate height of table header
        const oneRowHeight = 30; // Approximate height of one table row
        const minRequiredHeight = escalatedHeadingHeight + tableHeaderHeight + oneRowHeight;

        // A4 landscape page height is approximately 1122px
        const pageHeight = 1122;
        const remainingSpace = pageHeight - (cameraHeight % pageHeight);

        console.log(`Camera height: ${cameraHeight}, Remaining space: ${remainingSpace}, Required: ${minRequiredHeight}`);

        // If not enough space for heading + table header + at least one row, force new page
        if (remainingSpace > minRequiredHeight) {
            escalatedSection.classList.add('force-new-page');
            console.log('Forcing Escalated Events to new page - not enough space');
        } else {
            escalatedSection.classList.remove('force-new-page');
            console.log('Keeping Escalated Events on current page - enough space');
        }
    };


    const applySmartLayout = (element) => {
        // Remove any empty grid items or containers
        const emptyContainers = element.querySelectorAll('.MuiGrid-root, .MuiBox-root, div');

        emptyContainers.forEach(container => {
            const hasContent = hasRealContent(container);
            const isVisible = container.offsetHeight > 0 || container.offsetWidth > 0;

            if (!hasContent && isVisible && container.id !== 'cover-page') {
                // Check if this is creating unnecessary space
                const children = container.children;
                let allChildrenEmpty = true;

                for (let child of children) {
                    if (hasRealContent(child) || child.offsetHeight > 10) {
                        allChildrenEmpty = false;
                        break;
                    }
                }

                if (allChildrenEmpty) {
                    container.style.display = 'none';
                    container.style.height = '0';
                    container.style.minHeight = '0';
                }
            }
        });

        // Compact the layout
        const mainContent = element.querySelector('#pdf-content');
        if (mainContent) {
            mainContent.style.padding = '5px';
            mainContent.style.margin = '0';
        }

        // Apply avoid-break classes to tables and important sections
        const tables = element.querySelectorAll('table');
        tables.forEach(table => {
            table.classList.add('avoid-break');
        });

        const tableSections = element.querySelectorAll('.MuiBox-root');
        tableSections.forEach(section => {
            if (section.querySelector('table')) {
                section.classList.add('avoid-break');
            }
        });
    };

    const hasRealContent = (element) => {
        if (!element) return false;

        // Skip script and style tags
        if (element.tagName === 'SCRIPT' || element.tagName === 'STYLE') {
            return false;
        }

        // Check for visible text
        const text = element.textContent?.replace(/\s+/g, ' ').trim();
        if (text && text.length > 5) { // At least 5 characters of real text
            return true;
        }

        // Check for images, charts, tables
        if (element.tagName === 'IMG' ||
            element.tagName === 'TABLE' ||
            element.tagName === 'CANVAS' ||
            element.classList.contains('MuiTable-root')) {
            return true;
        }

        // Check children recursively
        for (let child of element.children) {
            if (hasRealContent(child)) {
                return true;
            }
        }

        return false;
    };
    const A4Sheet = {
        width: '297mm',
        //height: '420mm',   
        margin: 'auto',
        //padding: '20mm', 
        border: '1px solid rgba(145,158,171,0.24)',
        boxSizing: 'border-box',
        backgroundColor: '#ffffff'
        //display: 'flex', 
        //justifyContent: 'center', 
        //alignItems: 'center' 
    };

    const Content = {
        width: '100%',
        overflow: 'auto',
        maxHeight: '100vh',
        backgroundColor: '#ffffff',
    };


    const handleObservationKeyDown = (e) => {
        if (e.key === "Enter" && observationInput.trim()) {
            setObservations(prev => [...prev, observationInput.trim()]);
            setObservationInput("");
            e.preventDefault();
        }
    };

    const handleClearObservations = () => {
        setObservations([]);
    };

    const surveillanceStats = {
        totalEvents: '140',
        aiValidated: '100',
        escalated: '10',
        falseAlertsReduced: '45%',
        uptime: '99.9%',
        topZone: 'Gate-3 Entrance',
        commonIncident: 'Loitering',
    };

    const formatComment = (comment) => {
        
        if (!comment) return '';

        const emojiMap = {
            ':calendar:': '\u{1F4C5}',
            ':round_pushpin:': '\u{1F4CD}',
            ':rotating_light:': '\u{1F6A8}',
            ':dart:': '\u{1F3AF}',
            ':satellite_antenna:': '\u{1F4E1}',
            ':pushpin:': '\u{1F4CC}',
            ':loud_sound:': '\u{1F50A}',
            ':blue_book:': '\u{1F4D8}',
            ':memo:': '\u{1F4DD}',
        };

        const commentWithEmojis = Object.entries(emojiMap).reduce(
            (formattedComment, [shortcode, emoji]) => formattedComment.replaceAll(shortcode, emoji),
            comment
        );

        // Detect if comment contains HTML tags
        const hasHTML = /<\/?[a-z][\s\S]*>/i.test(commentWithEmojis);

        // If it's plain text, just replace \n with <br>
        if (!hasHTML) {
            return commentWithEmojis.replace(/\n/g, '<br>');
        }

        // Otherwise, it's HTML — inject inline list + paragraph styles
        // We normalize spacing and indentation
        return commentWithEmojis
            .replaceAll('<ul', '<ul style="margin:0;padding-left:16px;list-style-type:disc;"')
            .replaceAll('<ol', '<ol style="margin:0;padding-left:16px;list-style-type:decimal;"')
            .replaceAll('<li', '<li style="margin-bottom:2px;"')
            .replaceAll('<p', '<p style="margin:0 0 4px 0;"');
    }



    return (
        <>
            <style>
                {`
    .tableCol {
        padding-top: 4px !important;
        padding-bottom: 4px !important;
    }

    /* PDF-only overrides (applied via .pdf-export on #pdf-content clone) */
    .pdf-export .pdf-body,
    .pdf-export .pdf-body * {
        color: #000000 !important;
    }

    .pdf-export .pdf-body,
    .pdf-export .pdf-body .MuiTable-root,
    .pdf-export .pdf-body .MuiTableRow-root,
    .pdf-export .pdf-body .MuiTableCell-root,
    .pdf-export .pdf-body .MuiBox-root,
    .pdf-export .pdf-body .MuiGrid-root {
        background-color: #ffffff !important;
    }

    .pdf-export #cover-page,
    .pdf-export #cover-page * {
        color: #ffffff !important;
    }
    
    /* Default section behavior */
    .section-with-table {
        page-break-inside: auto;
        break-inside: auto;
    }
    
    /* Force new page when needed */
    .force-new-page {
        page-break-before: always !important;
        break-before: page !important;
    }
    
    /* Table styling with smart breaks */
    table {
        page-break-inside: auto;
        break-inside: auto;
        width: 100%;
        border-collapse: collapse;
    }
    
    /* Keep table headers with first row */
    thead {
        display: table-header-group;
    }
    
    /* Prevent table rows from breaking */
    tr {
        page-break-inside: avoid;
        break-inside: avoid;
    }
    
    /* Allow breaking for very long tables (15+ rows) */
    .section-with-table:has(table tr:nth-child(n+15)) tr {
        page-break-inside: auto;
        break-inside: auto;
    }
    
    /* Prevent breaking inside table cells */
    td, th {
        page-break-inside: avoid;
        break-inside: avoid;
    }
    
    /* Observations section styling */
    .observations-container {
        page-break-inside: avoid;
        break-inside: avoid;
    }
    
    .observation-item {
        page-break-inside: avoid;
        break-inside: avoid;
        margin-bottom: 8px;
    }
    
    /* Allow breaking for very long observations (10+ items) */
    .observations-section:has(.observation-item:nth-child(n+10)) .observation-item {
        page-break-inside: auto;
        break-inside: auto;
    }
    
    @media print {
        .force-new-page {
            page-break-before: always !important;
        }
        
        .section-with-table {
            page-break-inside: auto;
        }
        
        /* Ensure tables don't break awkwardly */
        table {
            page-break-inside: auto;
        }
        
        tr {
            page-break-inside: avoid;
        }
    }
`}
            </style>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <Box sx={{ paddingLeft: '1rem' }}>
                        <Button onClick={downloadPDF} variant="contained">Download PDF</Button>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <Box sx={{ ...A4Sheet }}>
                        <Box sx={{ ...Content }}>
                            <Box>
                                <Box
                                    ref={pdfRef}
                                    id="pdf-content"
                                    sx={{
                                        fontFamily: "Calibri, 'Segoe UI', Tahoma, sans-serif",
                                        fontSize: "small",
                                        padding: '10px',
                                        overflow: 'hidden',
                                        backgroundColor: '#ffffff',
                                    }}
                                >

                                    <Box
                                        sx={{
                                            height: '200mm',
                                            color: '#fff',
                                    //         background: `
                                    // linear-gradient(to bottom, #33214d, #382760),
                                    // radial-gradient(white, rgba(255,255,255,.2) 1px, transparent 1px),
                                    // radial-gradient(white, rgba(255,255,255,.15) 1px, transparent 1px),
                                    // radial-gradient(white, rgba(255,255,255,.1) 1px, transparent 1px)
                                    // `,
                                    background:'#41667D',
                                    backgroundSize: `
                                    cover,
                                    200px 200px,
                                    300px 300px,
                                    400px 400px
                                    `,
                                    backgroundPosition: `
                                    0 0,
                                    -10px -10px,
                                    -30px -30px,
                                    -70px -70px
                                    `,
                                            textAlign: 'center',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}
                                        id="cover-page"
                                    >

                                        {/* <Typography variant="h2" sx={{ 
                                fontWeight: 'bold', 
                                letterSpacing: '2px',
                                mb: 4,
                                textShadow: '0 0 10px rgba(255,255,255,0.5)'
                            }}>
                                Logo
                            </Typography> */}

                                       
                                       <Logo sx={{ width: 300, height: 'auto' }} />


                                        <Typography variant="h4" sx={{
                                            fontWeight: 'bold',
                                           
                                            //textShadow: '0 0 8px rgba(255,255,255,0.4)'
                                        }}>
                                            Monthly Surveillance Report
                                        </Typography>

                                        <Box sx={{
                                            position: 'absolute',
                                            bottom: '80px',
                                            width: '100%',
                                            //textShadow: '0 0 5px rgba(255,255,255,0.3)'
                                        }}>
                                            <Typography variant="subtitle1">
                                                {clientName} {Location ? ' - ' + Location : ''}
                                            </Typography>
                                            <Typography variant="subtitle2">
                                                {Month} - {Year}
                                            </Typography>
                                        </Box>
                                    </Box>


                                    <Box className="pdf-body">
                                    <Grid sx={{ color: 'white', textAlign: 'center', marginTop: '10px' }}>
                                        <Grid container>
                                            <Grid item xs={12}>
                                                <Typography style={{ fontWeight: "bold", color: 'black', textAlign: 'left' }}>
                                                    Executive Summary:
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                        <Grid>
                                            <Grid container sx={{ textAlign: 'left', color: '#008B8B', fontSize: '14px' }}>
                                                <Grid item xs={12}>
                                                    <Typography variant="inherit">Surveillance Performance | Last 30 Days</Typography>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Typography variant="inherit">Total Events Processed: {total_event_count}</Typography>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Typography variant="inherit">Command Centre-Validated Events: {true_event_count}</Typography>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Typography variant="inherit">Incidents Escalated by Analysts: {escalated_event_count}</Typography>
                                                </Grid>

                                                {/* <Grid item xs={12}>
                                        <Typography variant="inherit">False Alerts Reduced by: {surveillanceStats.falseAlertsReduced}</Typography>
                                        </Grid> */}
                                                {clientId && (Location === "") &&
                                                    <Grid item xs={12}>
                                                        <Typography variant="inherit">System Uptime: {uptime_percentage}%</Typography>
                                                    </Grid>
                                                }

                                                <Grid item xs={12}>
                                                    <Typography variant="inherit">Top Activity Zone: {top_camera_name}</Typography>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Typography variant="inherit">Most Common Incident Type: {top_event_type}</Typography>
                                                </Grid>

                                                <Grid item xs={12} sx={{ color: '#fff', marginTop: '10px' }}>
                                                    <Typography variant="inherit">
                                                        VIDISKY’s AI-led system continues to deliver precision surveillance — cutting through non-critical noise,
                                                        accelerating detection, and enabling faster human response.
                                                    </Typography>
                                                    <Typography variant="inherit">
                                                        This report covers detailed camera-level breakdowns, escalated events, and actionable insights to further
                                                        strengthen your on-site security.
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid container sx={{ marginTop: '4px', fontSize: '14px' }}>
                                            <Grid item xs={12}>
                                                <Typography component="h1" style={{ fontWeight: "bold", color: 'black', textAlign: 'left' }}>
                                                    Performance At a Glance:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="inherit" sx={{ textAlign: 'left', color: '#fff' }}>Our AI eliminates the noise, surfacing only actionable events for our monitoring agents — who then verify and escalate what truly matters. </Typography>
                                            </Grid>

                                        </Grid>
                                    </Grid>
                                    <Table sx={{ marginTop: '10px' }}>
                                        {/* bar chart */}
                                        <TableBody>
                                            <TableRow>
                                                <TableCell colSpan={4} style={{ textAlign: 'center', backgroundColor: 'white' }}>
                                                    <Box
                                                        sx={{
                                                            border: '1px solid #ccc',
                                                            borderRadius: '8px',
                                                            padding: 2,
                                                            backgroundColor: '#ffffff',
                                                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                                        }}
                                                    >
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'black', mb: 2 }}>
                                                            Protocol Wise - Event Dashboard
                                                        </Typography>

                                                        <Box sx={{ height: '400px', width: '900px' }}>
                                                            <Bar
                                                                data={chartData}
                                                                options={{
                                                                    ...options,
                                                                    animation: false,
                                                                    responsive: true,
                                                                    maintainAspectRatio: false,
                                                                }}
                                                                plugins={[ChartDataLabels]}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>

                                    </Table>



                                    <Box sx={{ marginTop: '40px' }} className="section-with-table" id="camera-wise-section">
                                        <Typography component="h1" style={{ fontWeight: "bold", color: '#000', textAlign: 'left', marginBottom: '12px' }}>
                                            Camera Wise Detailed View
                                        </Typography>
                                        <Box sx={{ fontSize: '12px' }}>
                                            <Table size="small" sx={{ border: '1px solid #b1b1b1', mb: 4 }}>
                                                <TableHead>
                                                    <TableRow
                                                        sx={{
                                                            
                                                            '& .MuiTableCell-root': {
                                                                color: tableHeaderTextColor,
                                                                height: '35px',
                                                                minHeight: '10px',
                                                                paddingTop: '6px',
                                                                paddingBottom: '5px',
                                                                paddingLeft: '5px',
                                                                paddingRight: '5px',
                                                                fontSize: '12px',
                                                                fontWeight: 'bold',
                                                                border: '1px solid #b1b1b1',
                                                                textAlign: 'center',
                                                                verticalAlign: 'middle',
                                                                whiteSpace: 'nowrap',
                                                                lineHeight: '1.5',
                                                            }
                                                        }}
                                                    >
                                                        <TableCell sx={{ backgroundColor: tableCellColor}}>Camera Name</TableCell>
                                                        <TableCell sx={{ backgroundColor: tableCellColor}}>Reviewed - Escalation Not Required</TableCell>
                                                        <TableCell sx={{ backgroundColor: tableCellColor}}>Reviewed - Escalation Required</TableCell>
                                                        <TableCell sx={{ backgroundColor: tableCellColor}}>Grand Total</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {cameraWiseData.length > 0 ? cameraWiseData.map((row, index) => (
                                                        <TableRow key={index} sx={{ backgroundColor: '#ffffff' }}>
                                                            <TableCell
                                                                className="tableCol"
                                                                sx={{
                                                                    fontSize: 'inherit',
                                                                    border: '1px solid #b1b1b1',
                                                                    color: '#000',
                                                                    textAlign: 'center',
                                                                    verticalAlign: 'middle',
                                                                    pt: 0,
                                                                    pb: 1.5,
                                                                    px: 1,
                                                                }}
                                                            >
                                                                {row.devicename}
                                                            </TableCell>
                                                            <TableCell
                                                                className="tableCol"
                                                                sx={{
                                                                    fontSize: 'inherit',
                                                                    border: '1px solid #b1b1b1',
                                                                    color: '#000',
                                                                    textAlign: 'center',
                                                                    verticalAlign: 'middle',
                                                                    pt: 0,
                                                                    pb: 1.5,
                                                                    px: 1,
                                                                }}
                                                            >
                                                                {row.false_event_count}
                                                            </TableCell>
                                                            <TableCell
                                                                className="tableCol"
                                                                sx={{
                                                                    fontSize: 'inherit',
                                                                    border: '1px solid #b1b1b1',
                                                                    color: '#000',
                                                                    textAlign: 'center',
                                                                    verticalAlign: 'middle',
                                                                    pt: 0,
                                                                    pb: 1.5,
                                                                    px: 1,
                                                                }}
                                                            >
                                                                {row.true_event_count}
                                                            </TableCell>
                                                            <TableCell
                                                                className="tableCol"
                                                                sx={{
                                                                    fontSize: 'inherit',
                                                                    border: '1px solid #b1b1b1',
                                                                    color: '#000',
                                                                    textAlign: 'center',
                                                                    verticalAlign: 'middle',
                                                                    pt: 0,
                                                                    pb: 1.5,
                                                                    px: 1,
                                                                }}
                                                            >
                                                                {row.total_audits}
                                                            </TableCell>
                                                        </TableRow>
                                                    )) : <TableRow>
                                                        <TableCell colSpan={4} style={{ color: '#fff', border: "1px solid black", padding: "5px", fontSize: 'inherit' }} align='center'>
                                                            All cameras were actively monitored, and we’re happy to report no major events occurred
                                                        </TableCell>
                                                    </TableRow>}
                                                    {cameraWiseData.length > 0 && <TableRow sx={{ backgroundColor: '#ffffff' }}>
                                                        <TableCell
                                                            className="tableCol"
                                                            sx={{
                                                                fontSize: 'inherit',
                                                                fontWeight: 'bold',
                                                                border: '1px solid #b1b1b1',
                                                                color: '#000',
                                                                textAlign: 'center',
                                                                verticalAlign: 'middle',
                                                                pt: 0,
                                                                pb: 1.5,
                                                                px: 1,
                                                            }}
                                                        >
                                                            Grand Total
                                                        </TableCell>
                                                        <TableCell
                                                            className="tableCol"
                                                            sx={{
                                                                fontSize: 'inherit',
                                                                fontWeight: 'bold',
                                                                border: '1px solid #b1b1b1',
                                                                color: '#000',
                                                                textAlign: 'center',
                                                                verticalAlign: 'middle',
                                                                pt: 0,
                                                                pb: 1.5,
                                                                px: 1,
                                                            }}
                                                        >
                                                            {grandTotalNotRequired}
                                                        </TableCell>
                                                        <TableCell
                                                            className="tableCol"
                                                            sx={{
                                                                fontSize: 'inherit',
                                                                fontWeight: 'bold',
                                                                border: '1px solid #b1b1b1',
                                                                color: '#000',
                                                                textAlign: 'center',
                                                                verticalAlign: 'middle',
                                                                pt: 0,
                                                                pb: 1.5,
                                                                px: 1,
                                                            }}
                                                        >
                                                            {grandTotalRequired}
                                                        </TableCell>
                                                        <TableCell
                                                            className="tableCol"
                                                            sx={{
                                                                fontSize: 'inherit',
                                                                fontWeight: 'bold',
                                                                border: '1px solid #b1b1b1',
                                                                color: '#000',
                                                                textAlign: 'center',
                                                                verticalAlign: 'middle',
                                                                pt: 0,
                                                                pb: 1.5,
                                                                px: 1,
                                                            }}
                                                        >
                                                            {grandTotalAll}
                                                        </TableCell>
                                                    </TableRow>}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    </Box>



                                    {/* <Box style={{ width: "100%", fontFamily: "Calibri", fontSize: "small" }} id="tblInformation"> */}
                                    <Box sx={{ marginTop: '10px' }} className="section-with-table" id="escalated-events-section" >
                                        <Grid container sx={{ marginBottom: '5px' }}>
                                            <Grid item xs={12}>
                                                <Typography component="h1" style={{ fontWeight: "bold", color: 'black', textAlign: 'left' }}>
                                                    Escalated Events Detailed View
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                        <Box sx={{ fontSize: '12px' }}>
                                            <Table sx={{ marginTop: '15px' }}>

                                                <TableHead>
                                                    <TableRow
                                                        sx={{
                                                            backgroundColor: '#5c7fff',
                                                            '& .MuiTableCell-root': {
                                                                color: tableHeaderTextColor,
                                                                height: '35px',
                                                                minHeight: '10px',
                                                                paddingTop: '6px',
                                                                paddingBottom: '5px',
                                                                paddingLeft: '5px',
                                                                paddingRight: '5px',
                                                                fontSize: '12px',
                                                                fontWeight: 'bold',
                                                                border: '1px solid #b1b1b1',
                                                                textAlign: 'center',
                                                                verticalAlign: 'middle',
                                                                whiteSpace: 'nowrap',
                                                                lineHeight: '1.5',
                                                                display: 'table-cell',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                            }
                                                        }}
                                                    >
                                                        <TableCell sx={{ backgroundColor: tableCellColor }}>S No</TableCell>
                                                        <TableCell sx={{ backgroundColor: tableCellColor }}>Event No</TableCell>
                                                        <TableCell sx={{ backgroundColor: tableCellColor }}>Camera Name</TableCell>
                                                        <TableCell sx={{ backgroundColor: tableCellColor }}>Event Time</TableCell>
                                                        <TableCell sx={{ backgroundColor: tableCellColor }}>Event Type</TableCell>
                                                        <TableCell sx={{ backgroundColor: tableCellColor }}>Event Summary</TableCell>
                                                        <TableCell sx={{ backgroundColor: tableCellColor }}>Video</TableCell>
                                                    </TableRow>
                                                </TableHead>

                                                <TableBody>
                                                    {Data && Data[0]?.Details && Data[0]?.Details.length > 0 ?
                                                        Data[0]?.Details.map((item, index) => (
                                                            <TableRow key={index} sx={{ backgroundColor: '#ffffff', textAlign: 'left' }}>
                                                                <TableCell className="tableCol" sx={{
                                                                    fontSize: 'inherit',
                                                                    border: '1px solid #b1b1b1',
                                                                    color: '#000',
                                                                    textAlign: 'left',
                                                                    verticalAlign: 'middle',
                                                                    wordWrap: 'break-word',
                                                                    pt: 0,
                                                                    pb: 1.5,
                                                                    px: 1,
                                                                }}>
                                                                    {index + 1}
                                                                </TableCell>
                                                                <TableCell className="tableCol" sx={{
                                                                    fontSize: 'inherit',
                                                                    border: '1px solid #b1b1b1',
                                                                    color: '#000',
                                                                    textAlign: 'left',
                                                                    verticalAlign: 'middle',
                                                                    wordWrap: 'break-word',
                                                                    pt: 0,
                                                                    pb: 1.5,
                                                                    px: 1,
                                                                }}>
                                                                    {item.EventNo}
                                                                </TableCell>
                                                                <TableCell className="tableCol" sx={{
                                                                    fontSize: 'inherit',
                                                                    border: '1px solid #b1b1b1',
                                                                    color: '#000',
                                                                    textAlign: 'left',
                                                                    verticalAlign: 'middle',
                                                                    wordWrap: 'break-word',
                                                                    pt: 0,
                                                                    pb: 1.5,
                                                                    px: 1,
                                                                }}>
                                                                    {item.devicename}
                                                                </TableCell>
                                                                <TableCell className="tableCol" sx={{
                                                                    fontSize: 'inherit',
                                                                    border: '1px solid #b1b1b1',
                                                                    color: '#000',
                                                                    textAlign: 'left',
                                                                    verticalAlign: 'middle',
                                                                    wordWrap: 'break-word',
                                                                    pt: 0,
                                                                    pb: 1.5,
                                                                    px: 1,
                                                                    whiteSpace: 'nowrap',
                                                                }}>
                                                                    {formatEventTime(item.EventTime)}
                                                                </TableCell>
                                                                <TableCell className="tableCol" sx={{
                                                                    fontSize: 'inherit',
                                                                    border: '1px solid #b1b1b1',
                                                                    color: '#000',
                                                                    textAlign: 'left',
                                                                    verticalAlign: 'middle',
                                                                    wordWrap: 'break-word',
                                                                    pt: 0,
                                                                    pb: 1.5,
                                                                    px: 1,
                                                                    // whiteSpace: 'nowrap',
                                                                }}>
                                                                    {item.EscalatedEventType}
                                                                </TableCell>
                                                                <TableCell className="tableCol" sx={{
                                                                    fontSize: 'inherit',
                                                                    border: '1px solid #b1b1b1',
                                                                    color: '#000',
                                                                    textAlign: 'left',
                                                                    verticalAlign: 'middle',
                                                                    wordWrap: 'break-word',
                                                                    pt: 0,
                                                                    pb: 1.5,
                                                                    px: 1,
                                                                }}>
                                                                    <div
                                                                        style={{
                                                                            whiteSpace: 'pre-wrap',
                                                                            fontFamily: 'Arial, Helvetica, sans-serif',
                                                                            lineHeight: 1.5,
                                                                            wordWrap: 'break-word',
                                                                            margin: 0,
                                                                            display: 'block',
                                                                            textAlign: 'left',
                                                                        }}
                                                                        dangerouslySetInnerHTML={{ __html: formatComment(item.Comment) }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell className="tableCol" sx={{
                                                                    fontSize: 'inherit',
                                                                    border: '1px solid #b1b1b1',
                                                                    textAlign: 'left',
                                                                    verticalAlign: 'middle',
                                                                    wordWrap: 'break-word',
                                                                    pt: 0,
                                                                    pb: 1.5,
                                                                    px: 1,
                                                                }}>                                                                
                                                                    <a
                                                                        href={item.VideoUrl}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        style={{ textDecoration: 'none', color: 'blue' }}>
                                                                        Play
                                                                    </a>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                        :
                                                        <TableRow>
                                                            <TableCell colSpan={7} style={{ color: 'black', border: "1px solid black", padding: "5px", fontSize: 'inherit' }} align='center'>
                                                                All cameras were actively monitored, and we’re happy to report no major events occurred
                                                            </TableCell>
                                                        </TableRow>
                                                    }
                                                </TableBody>

                                            </Table>
                                        </Box>
                                    </Box>
                                    {/* </Box> */}
                                    <Grid container sx={{ marginTop: '10px', marginBottom: '5px' }}>
                                        <Grid item xs={12} style={{ display: hideHeading ? 'none' : 'block' }}>
                                            <Typography component="h1" style={{ fontWeight: "bold", color: 'black', textAlign: 'left' }}>
                                                Observations and Recommendations
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12} className="no-print" style={{ display: hideInputs ? 'none' : 'block' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: 1 }}>
                                            <input
                                                type="text"
                                                placeholder="Type your observation and press Enter"
                                                value={observationInput}
                                                onChange={(e) => setObservationInput(e.target.value)}
                                                onKeyDown={handleObservationKeyDown}
                                                style={{ flexGrow: 1, padding: '8px', borderRadius: '4px', border: '1px solid gray' }}
                                            />
                                            <Button variant="outlined" onClick={handleClearObservations}>
                                                Clear
                                            </Button>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sx={{ marginTop: '10px' }}>
                                        {observations.map((item, idx) => (
                                            <Box key={idx} sx={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start' }}>
                                                <Box sx={{ color: '#000', fontSize: '18px', lineHeight: '1.4', mr: 1 }}>•</Box>
                                                <Box
                                                    sx={{
                                                        display: 'inline-block',
                                                        backgroundColor: '#333333',
                                                        padding: '6px 12px',
                                                        borderRadius: '6px',
                                                        fontSize: '14px',
                                                        color: '#333',
                                                        fontStyle: 'italic',
                                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                                    }}
                                                >
                                                    {item}
                                                </Box>
                                            </Box>
                                        ))}


                                    </Grid>

                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Grid>

                <style>
                    {`
                        .tableCol {
                            padding-top: 4px !important;
                            padding-bottom:4px !important;
                        }
                    `}
                </style>
            </Grid>


        </>
    );
};

export default FormComponent;
